import { useMemo } from 'react';

import { Response } from '../services/backendService/types';

/**
 * Keep only responses that have finished server-side moderation. As more
 * responses are moderated post-submission, downstream counts grow
 * monotonically — avoiding the flicker that happens when unmoderated
 * responses are counted first and then later removed/dismissed.
 */
export const filterModeratedResponses = (responses: Response[]): Response[] =>
  responses.filter(r => r.isModerated === true);

/** Total number of whitespace-delimited words across the given responses. */
export const countWords = (responses: Response[]): number =>
  responses.reduce(
    (acc, res) => acc + res.response.split(/\s+/).filter(word => word.length > 0).length,
    0,
  );

/** Total number of drawing details across the given responses. */
export const countDrawingDetails = (responses: Response[]): number =>
  responses.reduce(
    (acc, res) => (res.drawingDetails ? acc + res.drawingDetails : acc),
    0,
  );

export type ModeratedResponseCounts = {
  moderatedResponses: Response[];
  wordCount: number;
  detailCount: number;
};

/**
 * Derives word/detail totals from the subset of responses that have cleared
 * moderation. Used by the post-activity summary so the displayed tally only
 * reflects content safe to show.
 */
const useModeratedResponseCounts = (responses: Response[]): ModeratedResponseCounts => {
  const moderatedResponses = useMemo(() => filterModeratedResponses(responses), [
    responses,
  ]);

  const wordCount = useMemo(() => countWords(moderatedResponses), [moderatedResponses]);

  const detailCount = useMemo(() => countDrawingDetails(moderatedResponses), [
    moderatedResponses,
  ]);

  return { moderatedResponses, wordCount, detailCount };
};

export default useModeratedResponseCounts;
